import { isEmpty } from 'lodash';
import React, { useMemo, useRef } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import DotsLoader from 'src/@vymo/ui/atoms/loader/indicator/dots';
import { Form } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { ReactComponent as NoMeeting } from 'src/assets/icons/noMeetings.svg';
import { Keys, locale } from 'src/i18n';
import { setCurrentEntityId, setDetails } from 'src/models/details/slice';
import { REVYRE_FORM } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderDialog } from 'src/workspace/slice';
import {
  getCalendarActivityDetails,
  getIsLoading,
  getRevyreActivityInputsAndGroups,
} from '../selectors';
import styles from './index.module.scss';

function RevyreActivityDetails({ entityId }) {
  const formRef = useRef(null);
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getIsLoading);

  const calendarItemData = useAppSelector(getCalendarActivityDetails);

  const { inputs, groups } = useAppSelector((state) =>
    getRevyreActivityInputsAndGroups(state, 'activity', calendarItemData),
  );

  const formValues = useMemo(() => {
    const valuesMap = {};
    if (!isEmpty(calendarItemData?.mutatedEntity)) {
      Object.keys(calendarItemData?.mutatedEntity).forEach((key) => {
        valuesMap[key] = {
          value: calendarItemData?.mutatedEntity[key],
        };
      });
    }

    return valuesMap;
  }, [calendarItemData?.mutatedEntity]);

  if (!calendarItemData) {
    return (
      <div className={styles.activityCalendarCard__content__noSelection}>
        <NoMeeting />
        <Text>{locale(Keys.ACTIVITY_CARD_PLACEHOLDER)}</Text>
      </div>
    );
  }

  if (isLoading && !calendarItemData) {
    return (
      <div className={styles.activityCalendarCard__content__noSelection}>
        <DotsLoader width="var(--dimensions-600)" />
        <Text>{locale(Keys.ACTIVITY_CARD_LOADING)}</Text>
      </div>
    );
  }

  const handleOpenRevyreForm = () => {
    dispatch(setCurrentEntityId(entityId));
    dispatch(
      setDetails({
        entityId,
        data: { payload: { ...calendarItemData } },
        revyre: true,
      }),
    );
    dispatch(
      renderDialog({
        id: REVYRE_FORM,
        props: {
          type: 'update',
          domainType: 'activity',
          activityType: calendarItemData?.mutatedEntity?.activityType,
        },
      }),
    );
  };

  return (
    <div className={styles.activityDetails}>
      {/* HEADER */}
      <div className={styles.activityDetails__header}>
        <Text type="h5">{calendarItemData?.mutatedEntity?.name}</Text>
        {calendarItemData?.mutatedEntity?.currentState?.stateId !==
          'COMPLETED' && (
          <Button
            disabled={!calendarItemData?.allowedActionsAndMetadata?.length}
            type="outlined"
            variant="subtle"
            onClick={handleOpenRevyreForm}
          >
            {locale(Keys.UPDATE)}
          </Button>
        )}
      </div>

      {inputs?.length > 0 ? (
        <div className={styles.activityDetails__content}>
          <Form
            ref={formRef}
            id="activityDetails"
            name="activityDetails"
            onChange={() => {}}
            config={{
              version: FormVersion.web,
              data: inputs,
              grouping: groups,
              fieldItemConfig: { showDisabledIcon: true },
              viewMode: true,
            }}
            className={styles.activityDetails__form}
            value={formValues}
          />
        </div>
      ) : (
        <div className={styles.activityCalendarCard__content__noSelection}>
          <NoMeeting />
          <Text>No data for this activity</Text>
        </div>
      )}
    </div>
  );
}
export default RevyreActivityDetails;
